import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/joy';
import FunctionsIcon from '@mui/icons-material/Functions';
import History from '../../components/History';
import useFunctions from '../../hooks/useFunctions';
import useKeyboardShortcuts from './useKeyboardShortcuts';

import FunctionsToolbar from './FunctionsToolbar';
import FunctionsEmptyState from './FunctionsEmptyState';
import FunctionsGrid from './FunctionsGrid';
import FunctionsList from './FunctionsList';
import {
  FunctionEditorModal,
  DeleteFunctionModal,
  LogsModal,
  HelpModal,
} from './modals';

const FunctionsPage = () => {
  // Custom hook for all function operations
  const {
    functions,
    loading,
    refreshing,
    fetchFunctions,
    getFunction,
    createFunction,
    updateFunction,
    deleteFunction,
    duplicateFunction,
    runFunction,
    isRunning,
    getFunctionStatus,
    validateFunctionName,
    // Logs
    logs,
    logsLoading,
    logFilters,
    setLogFilters,
    fetchLogs,
    deleteLogs,
    deleteAllLogs,
    markLogsAsRead,
    // View
    view,
    setView,
  } = useFunctions();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const searchInputRef = useRef(null);

  // Selection state
  const [selectedFunctionId, setSelectedFunctionId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal states
  const [modals, setModals] = useState({
    create: false,
    edit: false,
    delete: false,
    logs: false,
    help: false,
  });

  // Current function for modals
  const [currentFunction, setCurrentFunction] = useState(null);

  // Load functions on mount
  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  // Filter functions
  const filteredFunctions = useMemo(() => {
    return functions.filter((func) => {
      // Search filter
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        func.name.toLowerCase().includes(query) ||
        (func.description || '').toLowerCase().includes(query) ||
        (func.code || '').toLowerCase().includes(query);

      // Type filter
      const matchesType = !typeFilter || func.function_type === typeFilter;

      // Status filter
      let matchesStatus = true;
      if (statusFilter) {
        const status = getFunctionStatus(func);
        if (statusFilter === 'success') {
          matchesStatus = status.status === 'success';
        } else if (statusFilter === 'error') {
          matchesStatus = status.status === 'error';
        } else if (statusFilter === 'never') {
          matchesStatus = !func.last_executed;
        }
      }

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [functions, searchQuery, typeFilter, statusFilter, getFunctionStatus]);

  // Check if any modal is open
  const isModalOpen = Object.values(modals).some(Boolean);

  // Modal handlers
  const openModal = useCallback((modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: true }));
  }, []);

  const closeModal = useCallback((modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: false }));
    if (modalName === 'logs') {
      if (currentFunction) {
        markLogsAsRead(currentFunction.id);
      }
    }
  }, [currentFunction, markLogsAsRead]);

  // Action handlers
  const handleCreateNew = useCallback(() => {
    setCurrentFunction(null);
    openModal('create');
  }, [openModal]);

  const handleEdit = useCallback(async (func) => {
    const fullData = await getFunction(func.id);
    setCurrentFunction(fullData || func);
    openModal('edit');
  }, [getFunction, openModal]);

  const handleDelete = useCallback((func) => {
    setCurrentFunction(func);
    openModal('delete');
  }, [openModal]);

  const handleViewLogs = useCallback(async (func) => {
    setCurrentFunction(func);
    setLogFilters((prev) => ({ ...prev, page: 1, search: '', errorFilter: 'all', day: '' }));
    await fetchLogs(func.id, { page: 1, search: '', errorFilter: 'all', day: '' });
    openModal('logs');
  }, [openModal, fetchLogs, setLogFilters]);

  const handleRun = useCallback((func) => {
    runFunction(func.id);
  }, [runFunction]);

  const handleDuplicate = useCallback((func) => {
    duplicateFunction(func);
  }, [duplicateFunction]);

  // Form submit handlers
  const handleCreateSubmit = useCallback(async (data) => {
    return await createFunction(data);
  }, [createFunction]);

  const handleEditSubmit = useCallback(async (data) => {
    if (!currentFunction) return false;
    return await updateFunction(currentFunction.id, data);
  }, [currentFunction, updateFunction]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!currentFunction) return;
    await deleteFunction(currentFunction.id);
    closeModal('delete');
    setCurrentFunction(null);
    setSelectedFunctionId(null);
    setSelectedIds(new Set());
  }, [currentFunction, deleteFunction, closeModal]);

  // Logs handlers
  const handleLogFilterChange = useCallback((filters) => {
    if (currentFunction) {
      const newFilters = { ...logFilters, ...filters };
      fetchLogs(currentFunction.id, newFilters);
    }
  }, [currentFunction, logFilters, fetchLogs]);

  const handleDeleteSelectedLogs = useCallback(async (indices) => {
    if (!currentFunction) return;
    await deleteLogs(currentFunction.id, indices);
    await fetchLogs(currentFunction.id, logFilters);
  }, [currentFunction, deleteLogs, fetchLogs, logFilters]);

  const handleDeleteAllLogs = useCallback(async () => {
    if (!currentFunction) return;
    await deleteAllLogs(currentFunction.id);
    await fetchLogs(currentFunction.id, { page: 1 });
  }, [currentFunction, deleteAllLogs, fetchLogs]);

  const handleRefreshLogs = useCallback(async () => {
    if (!currentFunction) return;
    await fetchLogs(currentFunction.id, logFilters);
  }, [currentFunction, fetchLogs, logFilters]);

  const handleMarkAllLogsRead = useCallback(async () => {
    if (!currentFunction) return;
    await markLogsAsRead(currentFunction.id);
  }, [currentFunction, markLogsAsRead]);

  // Selection handlers
  const handleSelectOne = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedIds(new Set(filteredFunctions.map((f) => f.id)));
    } else {
      setSelectedIds(new Set());
    }
  }, [filteredFunctions]);

  const handleClearSelection = useCallback(() => {
    setSelectedFunctionId(null);
    setSelectedIds(new Set());
  }, []);

  // Card selection (for grid view)
  const handleCardSelect = useCallback((func) => {
    setSelectedFunctionId((prev) => (prev === func.id ? null : func.id));
  }, []);

  // Keyboard shortcut handlers
  const handleFocusSearch = useCallback(() => {
    searchInputRef.current?.querySelector('input')?.focus();
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (selectedFunctionId) {
      const func = functions.find((f) => f.id === selectedFunctionId);
      if (func) handleDelete(func);
    } else if (selectedIds.size === 1) {
      const id = Array.from(selectedIds)[0];
      const func = functions.find((f) => f.id === id);
      if (func) handleDelete(func);
    }
  }, [selectedFunctionId, selectedIds, functions, handleDelete]);

  const handleEditSelected = useCallback(() => {
    if (selectedFunctionId) {
      const func = functions.find((f) => f.id === selectedFunctionId);
      if (func) handleEdit(func);
    }
  }, [selectedFunctionId, functions, handleEdit]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    isModalOpen,
    onNew: handleCreateNew,
    onRefresh: fetchFunctions,
    onFocusSearch: handleFocusSearch,
    onDeleteSelected: handleDeleteSelected,
    onEditSelected: handleEditSelected,
    onClearSelection: handleClearSelection,
    selectedFunctionId,
    selectedIds,
  });

  // Loading state
  if (loading && functions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      className="MainContent"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <Stack sx={{ px: { xs: 2, md: 4 }, pt: '0.5%', flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <History />
        </Box>
        {/* Page Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 'lg',
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.vars.palette.primary[400]} 0%, ${theme.vars.palette.primary[600]} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'sm',
              }}
            >
              <FunctionsIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography level="h2" component="h1">
                Functions
              </Typography>
              <Typography level="body-sm" color="neutral">
                Run custom Python code on scheduled intervals or after extraction
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Toolbar */}
        <Box ref={searchInputRef}>
          <FunctionsToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            view={view}
            onViewChange={setView}
            onRefresh={fetchFunctions}
            onCreateNew={handleCreateNew}
            loading={refreshing}
          />
        </Box>
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: { xs: 2, md: 4 }, py: 1 }}>
        {functions.length === 0 ? (
          <FunctionsEmptyState onAddNew={handleCreateNew} />
        ) : view === 'grid' ? (
          <FunctionsGrid
            functions={filteredFunctions}
            loading={refreshing}
            getFunctionStatus={getFunctionStatus}
            isRunning={isRunning}
            selectedFunctionId={selectedFunctionId}
            onRun={handleRun}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onViewLogs={handleViewLogs}
            onSelect={handleCardSelect}
          />
        ) : (
          <FunctionsList
            functions={filteredFunctions}
            loading={refreshing}
            getFunctionStatus={getFunctionStatus}
            isRunning={isRunning}
            selectedIds={selectedIds}
            onRun={handleRun}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onViewLogs={handleViewLogs}
            onSelectOne={handleSelectOne}
            onSelectAll={handleSelectAll}
          />
        )}
      </Box>

      {/* Modals */}
      <FunctionEditorModal
        open={modals.create}
        onClose={() => closeModal('create')}
        onSubmit={handleCreateSubmit}
        onOpenHelp={() => openModal('help')}
        isEdit={false}
        validateName={validateFunctionName}
      />

      <FunctionEditorModal
        open={modals.edit}
        onClose={() => closeModal('edit')}
        onSubmit={handleEditSubmit}
        onOpenHelp={() => openModal('help')}
        initialData={currentFunction}
        isEdit={true}
        validateName={validateFunctionName}
      />

      <DeleteFunctionModal
        open={modals.delete}
        onClose={() => closeModal('delete')}
        onConfirm={handleDeleteConfirm}
        functionName={currentFunction?.name}
      />

      <LogsModal
        open={modals.logs}
        onClose={() => closeModal('logs')}
        functionName={currentFunction?.name}
        logs={logs}
        loading={logsLoading}
        logFilters={logFilters}
        onFilterChange={handleLogFilterChange}
        onDeleteSelected={handleDeleteSelectedLogs}
        onDeleteAll={handleDeleteAllLogs}
        onMarkAllRead={handleMarkAllLogsRead}
        onRefresh={handleRefreshLogs}
      />

      <HelpModal
        open={modals.help}
        onClose={() => closeModal('help')}
      />
    </Box>
  );
};

export default FunctionsPage;
